import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { throttle } from 'throttle-debounce';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import { EGDSIcon, EGDSIconSize } from '../../icons/icon/index.esm.js';
import '../egds-toolbar/index.esm.js';
import { EGDSToolbarContainer } from '../toolbar-container/index.esm.js';
import '../transparent-toolbar/index.esm.js';
import { EGDSToolbarButton } from '../toolbar-button/index.esm.js';
import { EGDSToolbarNavigationTypeIcons, EGDSToolbarPageMargins } from '../types/index.esm.js';
import styles from '../../styles/uitk-egds-toolbar.css';
import { useEGDSContext, EGDSContextWrapper } from '../../utils/context-provider/index.esm.js';
import { classNames } from '../../utils/classnames/index.esm.js';
const getClassNameList = classNames.bind(styles);
// To be cleaned up
var ToolbarHeaderType;
(function (ToolbarHeaderType) {
  ToolbarHeaderType["OVERLAY"] = "Overlay";
})(ToolbarHeaderType || (ToolbarHeaderType = {}));
var EGDSToolbarExpandedState;
(function (EGDSToolbarExpandedState) {
  EGDSToolbarExpandedState["EXPANDED"] = "expanded";
  EGDSToolbarExpandedState["COLLAPSED"] = "collapsed";
})(EGDSToolbarExpandedState || (EGDSToolbarExpandedState = {}));
const Toolbar = props => {
  const {
    id,
    className,
    contentDomRef,
    style,
    toolbarType,
    children,
    childClassName,
    navigationContent,
    pageMargin,
    toolbarActionContent,
    toolbarTitle,
    toolbarTitleTag,
    toolbarSubtitle,
    toolbarTitleA11yText,
    ...attributes
  } = props;
  const {
    data
  } = useEGDSContext();
  const baseClassName = 'uitk-toolbar';
  const [elevatedToolbar, setElevatedToolbar] = useState(false);
  const [scrollUnderElementBlockSizeStyle, setScrollUnderElementBlockSizeStyle] = useState({});
  const isElevatedToolbarRef = useRef(false);
  const handleScrollRef = useRef(null);
  let expandedState;
  const toggleElevatedState = isElevated => {
    setElevatedToolbar(isElevated);
    isElevatedToolbarRef.current = isElevated;
  };
  const handleScrollWithJs = event => {
    if (event.target.scrollTop > 0) {
      if (!isElevatedToolbarRef.current) {
        toggleElevatedState(true);
      }
    } else {
      toggleElevatedState(false);
    }
  };
  useEffect(() => {
    const domRef = contentDomRef;
    // Use JS to add a classname for scroll, that can
    // be leveraged if CSS scroll animations aren't supported
    // Unfortunately, due to test fails CSS.supports() cannot be used
    // so both JS and non-JS methods must be employed here until full support is reached
    handleScrollRef.current = throttle(100, handleScrollWithJs);
    domRef?.current?.addEventListener('scroll', handleScrollRef.current);
    return () => domRef?.current?.removeEventListener('scroll', handleScrollRef.current);
  }, [contentDomRef, handleScrollRef]);
  // Set a CSS custom property for scroll animations
  useEffect(() => {
    const setScrollUnderElementBlockSizeVal = data?.scrollUnderElement?.current ? `${data?.scrollUnderElement?.current?.clientHeight}px` : null;
    setScrollUnderElementBlockSizeStyle({
      [`--scroll-under-element-blocksize`]: setScrollUnderElementBlockSizeVal
    });
  }, [data?.scrollUnderElement]);
  const containerClassNames = getClassNameList(className, toolbarType && `${baseClassName}-${toolbarType}`, children && `${baseClassName}-has-placeholder`, elevatedToolbar && `${baseClassName}-floating`, contentDomRef && `${baseClassName}-animated`, pageMargin && `${baseClassName}-has-page-margin`, data?.scrollUnderElement && `${baseClassName}-precedes-scroll-under-element`);
  const toolbarActionsClassname = getClassNameList(`${baseClassName}-actions`);
  const toolbarSubtitleClassname = getClassNameList(`${baseClassName}-subtitle`);
  const Tag = toolbarTitleTag || 'h2';
  const toolbarSubtitleContent = toolbarSubtitle && (typeof toolbarSubtitle === 'string' ? jsx("p", {
    className: toolbarSubtitleClassname,
    "aria-hidden": !!data?.toolbarSubtitle,
    children: toolbarSubtitle
  }) : React.cloneElement(toolbarSubtitle, {
    className: toolbarSubtitleClassname,
    'aria-hidden': !!data?.toolbarSubtitle
  }));
  const {
    navType,
    navIconLabel,
    navButtonLabel,
    onClick,
    onClickAnalytics,
    navButtonRef,
    navButtonId
  } = navigationContent || {};
  const iconType = navType && EGDSToolbarNavigationTypeIcons.get(navType) || EGDSToolbarNavigationTypeIcons.get('close');
  if (navType === 'expand_down') expandedState = EGDSToolbarExpandedState.EXPANDED;
  if (navType === 'expand_up') expandedState = EGDSToolbarExpandedState.COLLAPSED;
  const pageMarginStylevalue = inputval => {
    const outputValue = {};
    const propBaseName = '--egds-toolbar-page-margin';
    const inputIsString = typeof inputval === 'string';
    if (inputIsString) {
      Object.assign(outputValue, {
        [propBaseName]: `var(${EGDSToolbarPageMargins.get(inputval)})`
      });
    } else {
      Object.entries(inputval).map(([key, value]) => Object.assign(outputValue, {
        [`${propBaseName}-${key}`]: `var(${EGDSToolbarPageMargins.get(value)})`
      }));
    }
    return outputValue;
  };
  const toolbarStyles = {
    ...style,
    ...(pageMargin && pageMarginStylevalue(pageMargin)),
    ...scrollUnderElementBlockSizeStyle
  };
  return jsx(EGDSContextWrapper, {
    children: () => jsxs(EGDSToolbarContainer, {
      id: id,
      className: containerClassNames,
      style: toolbarStyles,
      ...attributes,
      children: [navigationContent && onClick && jsxs(EGDSToolbarButton, {
        onClick: onClick,
        onClickAnalytics: onClickAnalytics,
        buttonId: navButtonId,
        buttonRef: navButtonRef,
        expandedState: expandedState ?? undefined,
        containerId: expandedState ? id : undefined,
        children: [jsx(EGDSIcon, {
          id: `${navButtonId}-${iconType}-toolbar`,
          name: iconType,
          size: EGDSIconSize.MEDIUM,
          title: navIconLabel
        }), navButtonLabel]
      }), jsxs("section", {
        className: getClassNameList(`${baseClassName}-titles`),
        children: [toolbarTitle && jsx(Tag, {
          className: getClassNameList(`${baseClassName}-title`),
          "aria-hidden": toolbarTitleA11yText || data?.toolbarTitle ? 'true' : 'false',
          children: toolbarTitle
        }), toolbarTitleA11yText && jsx(Tag, {
          className: getClassNameList('is-visually-hidden'),
          children: toolbarTitleA11yText
        }), toolbarSubtitle && toolbarSubtitleContent]
      }), toolbarActionContent && jsx("div", {
        className: toolbarActionsClassname,
        children: toolbarActionContent
      }), children && jsx("section", {
        className: getClassNameList(`${baseClassName}-children`, childClassName),
        children: children
      })]
    })
  });
};
Toolbar.displayName = 'EGDSToolbar';
Toolbar.defaultProps = {
  pageMargin: 'small'
};
export { EGDSToolbarExpandedState, Toolbar, ToolbarHeaderType };